import { UserIcon } from 'lucide-react-native';
import type { ReactNode } from 'react';
import {
    Avatar as TamaguiAvatar,
    SizableText,
    type SquareProps,
    View,
} from 'tamagui';

import { isEmpty } from '../../misc';
import { Icon } from '../icons/Icon';

export type MemberAvatarSize = 'small' | 'medium' | 'large';

interface MemberAvatarProps extends SquareProps {
    text?: string | null;
    imageSrc?: string;
    avatarSize: MemberAvatarSize;
    withBorder?: boolean;
}

/**
 * @deprecated MemberAvatar is deprecated. Use a simple Image component with circular styling instead.
 * For guide avatars, use GuideBlurb which has built-in avatar support.
 */
export const MemberAvatar = ({
    imageSrc,
    text,
    withBorder = false,
    avatarSize,
    ...props
}: MemberAvatarProps) => {
    const size =
        avatarSize === 'large'
            ? '$8'
            : avatarSize === 'medium'
              ? '$4.75'
              : '$3';
    const fontSize =
        avatarSize === 'large' ? '$5' : avatarSize === 'medium' ? '$3' : '$1';
    const iconSize =
        avatarSize === 'large' ? 20 : avatarSize === 'medium' ? 16 : 12;

    // Image Avatar
    if (imageSrc != null) {
        return (
            <TamaguiAvatar
                circular
                size={size}
                borderWidth={withBorder ? 1 : 0}
                borderColor={withBorder ? '$gray300' : 'transparent'}
                {...props}
            >
                <TamaguiAvatar.Image src={imageSrc} />
                <TamaguiAvatar.Fallback backgroundColor="$gray300" />
            </TamaguiAvatar>
        );
    }

    let content: ReactNode;

    if (!isEmpty(text)) {
        content = (
            <SizableText size={fontSize} fontWeight="600">
                {(text ?? '').slice(0, 2)}
            </SizableText>
        );
    } else {
        content = <Icon color="$gray1100" icon={UserIcon} size={iconSize} />;
    }

    // Text Avatar
    return (
        <TamaguiAvatar circular size={size} {...props}>
            <View
                width="100%"
                height="100%"
                backgroundColor="$gray300"
                justifyContent="center"
                alignItems="center"
            >
                {content}
            </View>
        </TamaguiAvatar>
    );
};

interface MemberAvatarGroupProps {
    topAvatar: Omit<MemberAvatarProps, 'avatarSize'>;
    bottomAvatar: Omit<MemberAvatarProps, 'avatarSize'>;
    avatarSize: MemberAvatarSize;
}

/**
 * @deprecated MemberAvatarGroup is deprecated. Use a simple Image component with circular styling instead.
 */
export const MemberAvatarGroup = ({
    topAvatar,
    bottomAvatar,
    avatarSize,
}: MemberAvatarGroupProps) => {
    // There are manually tuned to achieve the desired layout.
    const bottom =
        avatarSize === 'large' ? 44 : avatarSize === 'medium' ? 24 : 16;
    const marginLeft =
        avatarSize === 'large' ? 44 : avatarSize === 'medium' ? 24 : 16;
    const borderWidth =
        avatarSize === 'large' ? 4 : avatarSize === 'medium' ? 2 : 1;
    const viewHeight =
        avatarSize === 'large' ? 112 : avatarSize === 'medium' ? 72 : 48;

    return (
        <View height={viewHeight}>
            <MemberAvatar
                {...topAvatar}
                avatarSize={avatarSize}
                zIndex={2}
                borderColor="white"
                borderWidth={borderWidth}
            />
            <MemberAvatar
                {...bottomAvatar}
                avatarSize={avatarSize}
                marginLeft={marginLeft}
                bottom={bottom}
                zIndex={1}
                borderColor="white"
                borderWidth={borderWidth}
            />
        </View>
    );
};
